import React, { useEffect, useState } from "react";
import "./ApiKeyManager.css";

const ApiKeyManager = ({ fetchWithAuth }) => {
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState("");
  const [generatedKey, setGeneratedKey] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const baseUrl = process.env.REACT_APP_FQDN;

  const fetchApiKeys = async () => {
    try {
      const res = await fetchWithAuth(`${baseUrl}/api/list_api_keys`);
      const data = await res.json();
      if (res.ok && data.api_keys) {
        setApiKeys(data.api_keys);
      } else {
        setApiKeys([]);
      }
    } catch (err) {
      console.error("Failed to fetch API keys", err);
      setMessage({ type: 'error', text: 'Failed to load API keys' });
    }
  };

  useEffect(() => {
    fetchApiKeys();
  }, []);

  const handleGenerateKey = async () => {
    if (!newKeyName.trim()) {
      setMessage({ type: 'error', text: 'API key name is required' });
      return;
    }

    setLoading(true);
    setGeneratedKey(null);
    setMessage({ type: '', text: '' });

    try {
      const res = await fetchWithAuth(`${baseUrl}/api/generate_api_key`, {
        method: "POST",
        body: JSON.stringify({ name: newKeyName }),
      });
      const data = await res.json();

      if (!res.ok) {
        setMessage({ type: 'error', text: data.error || data.message || 'Failed to create API key' });
        return;
      }

      setGeneratedKey(data.api_key);
      setNewKeyName("");
      setMessage({ type: 'success', text: 'API key generated successfully! Copy it now - you won\'t see it again.' });
      fetchApiKeys();
    } catch (err) {
      console.error("Error generating API key", err);
      setMessage({ type: 'error', text: 'Error generating API key. Please try again.' });
    } finally {
      setLoading(false);
    }
  };

  const handleRevokeKey = async (name) => {
    if (!window.confirm(`Are you sure you want to revoke API key "${name}"?`)) return;

    try {
      const res = await fetchWithAuth(`${baseUrl}/api/revoke_api_key`, {
        method: "POST",
        body: JSON.stringify({ name }),
      });
      const data = await res.json();

      if (!res.ok) {
        setMessage({ type: 'error', text: data.error || 'Failed to revoke key' });
        return;
      }

      setMessage({ type: 'success', text: `API key "${name}" revoked successfully` });
      fetchApiKeys();
    } catch (err) {
      console.error("Error revoking API key", err);
      setMessage({ type: 'error', text: 'Error revoking API key. Please try again.' });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      setMessage({ type: 'success', text: 'API key copied to clipboard!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    }).catch(() => {
      setMessage({ type: 'error', text: 'Failed to copy to clipboard' });
    });
  };

  return (
    <div className="api-key-manager">
      <h3>API Keys</h3>

      {message.text && (
        <div className={`form-message ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Create new key */}
      <div className="api-key-create">
        <div>
          <label className="form-field-label" htmlFor="keyName">API Key Name</label>
          <input
            id="keyName"
            type="text"
            value={newKeyName}
            onChange={(e) => {
              setNewKeyName(e.target.value);
              setMessage({ type: '', text: '' });
            }}
            placeholder="e.g., Production Key, Development Key"
            className="register-input"
            disabled={loading}
          />
          <small className="field-hint">Choose a descriptive name to identify this key</small>
        </div>

        <button
          onClick={handleGenerateKey}
          disabled={loading}
          className={`register-input ${loading ? 'loading' : ''}`}
        >
          {loading ? "Creating..." : "Generate API Key"}
        </button>
      </div>

      {/* Show generated key ONCE */}
      {generatedKey && (
        <div className="api-key-generated">
          <strong>New API Key Generated Successfully!</strong>
          <pre
            onClick={() => copyToClipboard(generatedKey)}
            title="Click to copy"
          >
            {generatedKey}
          </pre>
          <button
            onClick={() => copyToClipboard(generatedKey)}
            className="api-key-copy-btn"
          >
            Copy to Clipboard
          </button>
          <p style={{
            color: 'rgba(255, 255, 255, 0.9)',
            fontSize: '0.85rem',
            marginTop: '0.75rem',
            marginBottom: 0
          }}>
            Save this key now - you won't be able to see it again!
          </p>
        </div>
      )}

      {/* Existing keys */}
      <div className="api-key-list">
        {apiKeys.length === 0 ? (
          <p>No active API keys. Create one above to get started.</p>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Created</th>
                <th>Expires</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {apiKeys.map((key) => (
                <tr key={key.name}>
                  <td>
                    <div className="api-key-name">{key.name}</div>
                  </td>
                  <td>
                    <div className="api-key-date">
                      {new Date(key.created_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </div>
                  </td>
                  <td>
                    <div className="api-key-date">
                      {new Date(key.expires_at).toLocaleDateString('en-GB', {
                        day: 'numeric',
                        month: 'short',
                        year: 'numeric'
                      })}
                    </div>
                  </td>
                  <td>
                    <button
                      className="revoke-btn"
                      onClick={() => handleRevokeKey(key.name)}
                    >
                      Revoke
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default ApiKeyManager;
